import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { SetupDock } from './SetupDock';
import { useDesk } from '@/components/growth-desk/data/DeskData';
import { PageCoach } from './PageCoach';
import { NextStepNudge } from './NextStepNudge';
import { apiClient } from '@/services/api';
import { vibeApi } from '@/services/vibeApi';
import { OAuthApiClient } from '@/lib/oauth/OAuthApiClient';
import { nextSetupStep, type SetupFacts } from './useOnboardingState';
import { coachForRoute, type SetupStepCopy } from './overlayCopy';

const EMPTY_FACTS: SetupFacts = {
  hasBrandKit: false,
  creativeCount: 0,
  connectionCount: 0,
  publishedCount: 0,
};

export function SetupDockMount() {
  const { user, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs } = useDesk();
  const [facts, setFacts] = useState<SetupFacts>(EMPTY_FACTS);
  const [ready, setReady] = useState(false);
  // Set when a page tour finishes — the next unfinished setup step to offer.
  const [handoff, setHandoff] = useState<SetupStepCopy | null>(null);

  const coachId = useMemo(
    () => coachForRoute(location.pathname),
    [location.pathname]
  );

  // A tour on one page shouldn't leave its handoff hanging over another.
  useEffect(() => {
    setHandoff(null);
  }, [location.pathname]);

  const onCoachComplete = useCallback(() => {
    setHandoff(nextSetupStep(facts));
  }, [facts]);

  const goToStep = useCallback(
    (route: string) => {
      setHandoff(null);
      navigate(route);
    },
    [navigate]
  );

  useEffect(() => {
    if (!user || !currentOrganization) return;
    let cancelled = false;
    setReady(false);

    const fetchFacts = async () => {
      const oauthClient = new OAuthApiClient();
      const [brandResult, connResult, templResult, campResult] = await Promise.allSettled([
        vibeApi.getBrandProfile(),
        oauthClient.getConnections(),
        vibeApi.listTemplates({ custom_only: true, page_size: 1 }),
        apiClient.get('/api/v1/campaigns', { params: { limit: 50, offset: 0 } }),
      ]);

      if (cancelled) return;

      // Parse campaigns response — API returns nested structure
      let publishedCount = 0;
      if (campResult.status === 'fulfilled') {
        let convData = campResult.value.data?.conversations;
        let conversations: any[] = [];
        if (Array.isArray(convData)) {
          conversations = convData;
        } else if (convData?.conversations && Array.isArray(convData.conversations)) {
          conversations = convData.conversations;
        } else if (typeof convData === 'object' && convData !== null) {
          conversations = Object.values(convData).filter(
            (item: any) => typeof item === 'object' && item !== null
          );
        }
        publishedCount = conversations.filter(
          (c: any) => c.status && c.status !== 'draft'
        ).length;
      }

      setFacts({
        hasBrandKit:
          brandResult.status === 'fulfilled' &&
          Boolean(brandResult.value?.logo_url),
        connectionCount:
          connResult.status === 'fulfilled' ? connResult.value.length : 0,
        creativeCount:
          templResult.status === 'fulfilled'
            ? (templResult.value.total ?? templResult.value.templates.length)
            : 0,
        publishedCount,
      });
      setReady(true);
    };

    fetchFacts();
    return () => {
      cancelled = true;
    };
  }, [user?.id, currentOrganization?.id]);

  if (!user || !currentOrganization || !ready) return null;

  return (
    <>
      {coachId && (
        <PageCoach coachId={coachId} onComplete={onCoachComplete} />
      )}
      {handoff ? (
        <NextStepNudge
          step={handoff}
          onNavigate={goToStep}
          onDismiss={() => setHandoff(null)}
        />
      ) : (
        <SetupDock
          orgId={currentOrganization.id}
          facts={facts}
          onNavigate={(route) => navigate(route)}
          busy={jobs.length > 0}
        />
      )}
    </>
  );
}
